// This package does not have type definitions
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import jStat from "jstat";

/**
 * Performs a two-proportion Z-test for comparing two groups based on their
 * respective counts of "successes" (hits) within sample sizes. This is useful
 * when you want to determine whether the difference between two observed
 * proportions is statistically significant.
 *
 * @param controlSize - The sample size (number of trials) in the first group.
 * @param controlHits - The number of successes (hits) observed in the first group.
 * @param variantSize - The sample size (number of trials) in the second group.
 * @param variantHits - The number of successes (hits) observed in the second group.
 * @returns An object containing:
 *  - `zScore`: The computed Z-score for the difference between the two proportions.
 *  - `pValue`: The two-sided p-value derived from that Z-score.
 */
export function twoProportionZTest({
  controlSize,
  controlHits,
  variantSize,
  variantHits,
}: {
  controlSize: number;
  controlHits: number;
  variantSize: number;
  variantHits: number;
}) {
  // Pooled proportion
  const pPooled = (controlHits + variantHits) / (controlSize + variantSize);

  // Standard error of difference
  const standardError = Math.sqrt(
    pPooled * (1 - pPooled) * (1 / controlSize + 1 / variantSize),
  );

  // Individual proportions
  const pControl = controlHits / controlSize;
  const pVariant = variantHits / variantSize;

  // Z-score
  const zScore =
    standardError === 0 ? 0 : (pControl - pVariant) / standardError;

  // P-value
  const pValue = twoSidedPValueFromZScore(zScore);

  return { zScore, pValue };
}

/**
 * Convert a z-score to a two-sided p-value.
 */
function twoSidedPValueFromZScore(zScore: number) {
  const absZScore = Math.abs(zScore);

  const cdfValue: number = jStat.normal.cdf(absZScore, 0, 1);

  // Two-sided p-value = 2 * upper tail probability
  // = 2 * (1 - Φ(|z|))
  return 2 * (1 - cdfValue);
}
